/** #570 / #759 — read-only playUri confirmation probe after slow transport dispatch. */

import type { PlayUriAcceptanceEvidence, PlayUriConfirmOutcome, PlayUriPendingSession } from './playUriConfirmation';
import { hasStartConfirmPositionProgress, isStartConfirmGraceActive } from './playUriStartConfirmWindow';
import { stillTransitioning } from './playUriStartGates';

function transportStateEvidence(raw: string): PlayUriAcceptanceEvidence | null {
  const upper = String(raw || '').toUpperCase();
  if (upper.includes('PLAYING')) return 'renderer_playing';
  if (upper.includes('TRANSITIONING')) return 'renderer_transitioning';
  return null;
}

function graceStillStarting(
  session: PlayUriPendingSession,
  hasAcceptanceEvidence: boolean,
): boolean {
  return isStartConfirmGraceActive(session, hasAcceptanceEvidence);
}

function idleWhileStarting(
  session: PlayUriPendingSession,
  upper: string,
  hasAcceptanceEvidence: boolean,
): PlayUriConfirmOutcome | null {
  if (!upper.includes('STOPPED') && !upper.includes('NO_MEDIA')) return null;
  if (graceStillStarting(session, hasAcceptanceEvidence)) return 'pending';
  if (hasStartConfirmPositionProgress(session)) return 'success';
  return 'confirmed_failure';
}

/** #570 probe — STOPPED while prefetching is not failure when acceptance evidence exists. */
export async function probePlayUriConfirmation(
  getTransportInfo: (rendererId: string) => Promise<string>,
  session: PlayUriPendingSession,
  hasAcceptanceEvidence: boolean,
  noteEvidence: (evidence: PlayUriAcceptanceEvidence) => void,
): Promise<PlayUriConfirmOutcome> {
  const elapsedMs = Date.now() - session.dispatchedAt;

  if (hasAcceptanceEvidence) {
    try {
      const raw = await getTransportInfo(session.rendererId);
      const ev = transportStateEvidence(raw);
      if (ev) {
        noteEvidence(ev);
        if (ev === 'renderer_playing') return 'success';
      }
      if (stillTransitioning(ev, elapsedMs)) return 'pending';
      const idle = idleWhileStarting(session, String(raw || '').toUpperCase(), true);
      if (idle) return idle;
      if (elapsedMs < session.confirmWindowMs) return 'pending';
      return hasStartConfirmPositionProgress(session) ? 'success' : 'confirmed_failure';
    } catch {
      if (graceStillStarting(session, true)) return 'pending';
      return hasStartConfirmPositionProgress(session) ? 'success' : 'confirmed_failure';
    }
  }

  try {
    const raw = await getTransportInfo(session.rendererId);
    const ev = transportStateEvidence(raw);
    if (ev) {
      noteEvidence(ev);
      return ev === 'renderer_playing' ? 'success' : stillTransitioning(ev, elapsedMs) ? 'pending' : 'confirmed_failure';
    }
    const idle = idleWhileStarting(session, String(raw || '').toUpperCase(), false);
    if (idle) return idle;
  } catch {
    if (graceStillStarting(session, false)) return 'pending';
    return hasStartConfirmPositionProgress(session) ? 'success' : 'confirmed_failure';
  }

  if (elapsedMs >= session.confirmWindowMs) {
    return hasStartConfirmPositionProgress(session) ? 'success' : 'confirmed_failure';
  }
  return 'pending';
}
